import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage, getStatusLabel } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, 
  Plus, 
  MagnifyingGlass, 
  Eye,
  Trash,
  Barcode,
  X,
  ArrowRight,
  CheckSquare,
  UploadSimple,
  Warning,
  Camera,
  Image,
  MapPin,
  PencilSimple,
  CurrencyDollar
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { getPackages, createPackage, updatePackage, deletePackage, getCustomers, scanPackage, batchUpdateStatus, importPackagesCsv, uploadDeliveryProof, getDeliveryProof, sendPackageInvoice } from '../lib/api';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const STATUS_OPTIONS = [
  { value: 'submitted', label_en: 'Submitted', label_es: 'Enviado', label_fr: 'Soumis' },
  { value: 'intake_miami', label_en: 'Received at Warehouse', label_es: 'Recibido en Almacen', label_fr: 'Recu a l\'entrepot' },
  { value: 'in_transit', label_en: 'In Transit', label_es: 'En Transito', label_fr: 'En Transit' },
  { value: 'arrived_san_jose', label_en: 'Arrived in San Jose Warehouse', label_es: 'Llegó al Almacén de San José', label_fr: 'Arrivé à l\'entrepôt de San José' },
  { value: 'ready_for_pickup', label_en: 'Ready for Pickup', label_es: 'Listo para Recoger', label_fr: 'Pret a retirer' },
  { value: 'delivered', label_en: 'Delivered', label_es: 'Entregado', label_fr: 'Livre' }
];

const NEXT_STATUS = {
  submitted: 'intake_miami',
  intake_miami: 'in_transit',
  in_transit: 'arrived_san_jose',
  arrived_san_jose: 'ready_for_pickup',
  ready_for_pickup: 'delivered',
  delivered: null
};

const STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-700',
  intake_miami: 'bg-emerald-100 text-emerald-700',
  in_transit: 'bg-amber-100 text-amber-700',
  arrived_san_jose: 'bg-emerald-100 text-emerald-700',
  ready_for_pickup: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
};

export default function PackagesPage() {
  const { t, language , tx } = useLanguage();
  const { user } = useAuth();
  const isWarehouse = user?.role === 'warehouse';
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [tabFilter, setTabFilter] = useState('active'); // 'active' | 'completed'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ customer_id: '', weight_lbs: '', description: '', tracking_numbers_raw: '' });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    if (!bulkForm.customer_id) {
      toast.error(tx('Pick a client', 'Elige un cliente', 'Choisir un client'));
      return;
    }
    const lines = (bulkForm.tracking_numbers_raw || '')
      .split(/[\n,;\t]/)
      .map(s => s.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      toast.error(tx('Paste at least one tracking number', 'Pega al menos un tracking', 'Collez au moins un tracking'));
      return;
    }
    if (lines.length > 500) {
      toast.error(tx('Max 500 trackings per batch', 'Máx 500 por lote', 'Max 500 par lot'));
      return;
    }
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      const res = await api.post('/packages/bulk', {
        customer_id: bulkForm.customer_id,
        weight_lbs: Number(bulkForm.weight_lbs || 0),
        description: bulkForm.description,
        tracking_numbers: lines,
      });
      setBulkResult(res.data);
      const c = res.data.created_count, s = res.data.skipped_count;
      toast.success(tx(
        `${c} package(s) created · ${s} skipped · $${res.data.total_charge_usd.toFixed(2)} charged`,
        `${c} paquete(s) creados · ${s} omitidos · $${res.data.total_charge_usd.toFixed(2)}`,
        `${c} colis créés · ${s} ignorés`
      ), { duration: 8000 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Bulk add failed', 'Error', 'Échec'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkForm({ customer_id: '', weight_lbs: '', description: '', tracking_numbers_raw: '' });
    setBulkResult(null);
  };
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [priceOverridePkg, setPriceOverridePkg] = useState(null);
  const [priceOverrideDraft, setPriceOverrideDraft] = useState({ price: '', reason: '' });
  const [savingPriceOverride, setSavingPriceOverride] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    description: '',
    weight_lbs: '',
    weight_kg: '',
    dimensions: '',
    declared_value: '',
    notes: ''
  });
  const [deliveryProof, setDeliveryProof] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const proofInputRef = useRef(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [search, statusFilter, customerFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      
      const [packagesRes, customersRes] = await Promise.all([
        getPackages(params),
        getCustomers()
      ]);
      
      setPackages(packagesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error(tx('Failed to fetch data', 'Error al cargar datos', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryProof = async (packageId) => {
    setDeliveryProof(null);
    try {
      const res = await getDeliveryProof(packageId);
      setDeliveryProof(res.data);
    } catch {
      // No proof yet
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPackage) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setProofLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await uploadDeliveryProof(selectedPackage.id, { photo_data: reader.result });
        setDeliveryProof({ photo_data: reader.result, uploaded_at: new Date().toISOString() });
        toast.success(tx('Delivery proof uploaded', 'Prueba de entrega subida', 'Delivery proof uploaded'));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to upload');
      } finally {
        setProofLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        weight_lbs: parseFloat(formData.weight_lbs),
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        declared_value: formData.declared_value ? parseFloat(formData.declared_value) : null
      };
      await createPackage(data);
      toast.success(tx('Package created', 'Paquete creado', 'Colis créé'));
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Failed to create package', 'Error al crear paquete', 'Failed to create package')));
    }
  };

  // Weight prompt for arrived_san_jose status (when package arrives at the destination warehouse & is reweighed before pickup)
  const [weightPromptPkg, setWeightPromptPkg] = useState(null);
  const [weightPromptValue, setWeightPromptValue] = useState({ lbs: '', kg: '' });
  const [savingWeight, setSavingWeight] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  const handleSendPackageInvoice = async (pkg) => {
    if (!pkg) return;
    setSendingInvoice(true);
    try {
      const res = await sendPackageInvoice(pkg.id);
      const data = res?.data || {};
      if (data.email_sent) {
        toast.success(
          tx(`Invoice ${data.invoice_number} sent to ${data.to}`,
             `Factura ${data.invoice_number} enviada a ${data.to}`,
             `Facture ${data.invoice_number} envoyée à ${data.to}`)
        );
      } else {
        const reason = data.email_reason || tx('Email not configured', 'Email no configurado', 'Email non configuré');
        toast.warning(
          tx(`Invoice ${data.invoice_number} created — ${reason}`,
             `Factura ${data.invoice_number} creada — ${reason}`,
             `Facture ${data.invoice_number} créée — ${reason}`)
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to send invoice', 'Error al enviar factura', "Échec de l'envoi"));
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleUpdateStatus = async (packageId, newStatus) => {
    const pkg = packages.find(p => p.id === packageId);
    // When marking package as "Arrived in San Jose Warehouse", prompt the
    // tenant to confirm the actual weighed weight — this drives accurate
    // shipping cost calculation right before the final invoice + pickup step.
    if (newStatus === 'arrived_san_jose' && pkg) {
      setWeightPromptPkg(pkg);
      setWeightPromptValue({
        lbs: pkg.weight_lbs?.toString() || '',
        kg: pkg.weight_kg?.toString() || (pkg.weight_lbs ? (pkg.weight_lbs * 0.453592).toFixed(2) : ''),
      });
      return;
    }
    try {
      await updatePackage(packageId, { status: newStatus });
      toast.success(tx('Status updated', 'Estado actualizado', 'Status updated'));
      fetchData();
      if (selectedPackage?.id === packageId) {
        setSelectedPackage({ ...selectedPackage, status: newStatus });
      }
    } catch (error) {
      toast.error(tx('Failed to update', 'Error al actualizar', 'Failed to update'));
    }
  };

  const handleConfirmWeightAndIntake = async () => {
    if (!weightPromptPkg) return;
    const lbs = parseFloat(weightPromptValue.lbs);
    if (isNaN(lbs) || lbs <= 0) {
      toast.error(tx('Please enter a valid weight', 'Ingresa un peso válido', 'Saisissez un poids valide'));
      return;
    }
    setSavingWeight(true);
    try {
      const kg = weightPromptValue.kg ? parseFloat(weightPromptValue.kg) : parseFloat((lbs * 0.453592).toFixed(2));
      await updatePackage(weightPromptPkg.id, {
        status: 'arrived_san_jose',
        weight_lbs: lbs,
        weight_kg: kg,
      });
      toast.success(tx('Weight saved & status updated', 'Peso guardado y estado actualizado', 'Poids et statut mis à jour'));
      setWeightPromptPkg(null);
      setWeightPromptValue({ lbs: '', kg: '' });
      fetchData();
      if (selectedPackage?.id === weightPromptPkg.id) {
        setSelectedPackage({ ...selectedPackage, status: 'arrived_san_jose', weight_lbs: lbs, weight_kg: kg });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Failed to update', 'Error al actualizar', 'Failed to update'));
    } finally {
      setSavingWeight(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm(tx('Are you sure you want to delete this package?', 'Estas seguro de eliminar este paquete?', 'Are you sure you want to delete this package?'))) return;
    try {
      await deletePackage(packageId);
      toast.success(tx('Package deleted', 'Paquete eliminado', 'Colis supprimé'));
      setShowDetailModal(false);
      selectedIds.delete(packageId);
      setSelectedIds(new Set(selectedIds));
      fetchData();
    } catch (error) {
      toast.error(tx('Failed to delete', 'Error al eliminar', 'Failed to delete'));
    }
  };

  const openPriceOverride = (pkg) => {
    setPriceOverridePkg(pkg);
    setPriceOverrideDraft({ price: String(pkg.shipping_cost ?? ''), reason: pkg.price_override_reason || '' });
  };

  const closePriceOverride = () => {
    setPriceOverridePkg(null);
    setPriceOverrideDraft({ price: '', reason: '' });
  };

  const savePriceOverride = async () => {
    const val = parseFloat(priceOverrideDraft.price);
    if (Number.isNaN(val) || val < 0) {
      toast.error(tx('Enter a valid price', 'Ingresa un precio válido', 'Prix invalide'));
      return;
    }
    setSavingPriceOverride(true);
    try {
      const res = await api.put(`/packages/${priceOverridePkg.id}/price`, {
        shipping_cost: val,
        reason: priceOverrideDraft.reason,
      });
      toast.success(tx(`Price set to $${val.toFixed(2)}`, `Precio: $${val.toFixed(2)}`, `Prix : ${val.toFixed(2)}$`));
      closePriceOverride();
      // If detail modal is open for the same package, refresh it
      if (selectedPackage && selectedPackage.id === res.data.id) {
        setSelectedPackage(res.data);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to update price', 'Error al actualizar precio', 'Échec'));
    } finally {
      setSavingPriceOverride(false);
    }
  };

  const resetForm = () => {
    setFormData({ customer_id: '', description: '', weight_lbs: '', weight_kg: '', dimensions: '', declared_value: '', notes: '' });
  };

  // === BATCH STATUS ===
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === packages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(packages.map(p => p.id)));
    }
  };

  const getNextBatchStatus = () => {
    const selected = packages.filter(p => selectedIds.has(p.id));
    if (selected.length === 0) return null;
    const statuses = [...new Set(selected.map(p => p.status))];
    if (statuses.length === 1) return NEXT_STATUS[statuses[0]];
    return null;
  };

  const handleBatchAdvance = async () => {
    const nextStatus = getNextBatchStatus();
    if (!nextStatus) return;
    
    setBatchLoading(true);
    try {
      const res = await batchUpdateStatus({
        package_ids: [...selectedIds],
        status: nextStatus
      });
      const label = STATUS_OPTIONS.find(o => o.value === nextStatus);
      const statusName = language === 'fr' ? (label?.label_fr || label?.label_en) : language === 'es' ? label?.label_es : label?.label_en;
      toast.success(
        `${res.data.updated_count} ${tx('packages updated to', 'paquetes actualizados a', 'packages updated to')} ${statusName}`
      );
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Batch update failed', 'Error en actualización masiva', 'Batch update failed')));
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchSetStatus = async (status) => {
    setBatchLoading(true);
    try {
      const res = await batchUpdateStatus({
        package_ids: [...selectedIds],
        status
      });
      toast.success(`${res.data.updated_count} ${tx('packages updated', 'paquetes actualizados', 'packages updated')}`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Failed', 'Error', 'Failed')));
    } finally {
      setBatchLoading(false);
    }
  };

  // === CSV IMPORT ===
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseCSV(text);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      toast.error(tx('CSV empty or no data', 'CSV vacio o sin datos', 'CSV empty or no data'));
      return;
    }
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    const emailIdx = headers.findIndex(h => h.includes('email') || h.includes('correo'));
    const descIdx = headers.findIndex(h => h.includes('desc'));
    const weightIdx = headers.findIndex(h => h.includes('weight') || h.includes('peso'));
    const dimIdx = headers.findIndex(h => h.includes('dim'));
    const valIdx = headers.findIndex(h => h.includes('value') || h.includes('valor'));
    const notesIdx = headers.findIndex(h => h.includes('note') || h.includes('nota'));
    
    if (emailIdx === -1 || descIdx === -1 || weightIdx === -1) {
      toast.error(
        tx('CSV must have columns: email, description, weight_lbs', 'CSV debe tener columnas: email, description, weight_lbs', 'CSV must have columns: email, description, weight_lbs')
      );
      return;
    }
    
    const rows = [];
    const errors = [];
    
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map(c => c.trim().replace(/^['"]+|['"]+$/g, ''));
      if (cols.length < 3 || !cols[emailIdx]) continue;
      
      const weight = parseFloat(cols[weightIdx]);
      if (isNaN(weight) || weight <= 0) {
        errors.push({ row: i, msg: `Invalid weight: ${cols[weightIdx]}` });
        continue;
      }
      
      rows.push({
        customer_email: cols[emailIdx],
        description: cols[descIdx] || '',
        weight_lbs: weight,
        dimensions: dimIdx >= 0 ? (cols[dimIdx] || '') : '',
        declared_value: valIdx >= 0 ? (parseFloat(cols[valIdx]) || 0) : 0,
        notes: notesIdx >= 0 ? (cols[notesIdx] || '') : ''
      });
    }
    
    setCsvData(rows);
    setCsvErrors(errors);
    setCsvResult(null);
    setShowCsvModal(true);
  };

  const handleCsvImport = async () => {
    setCsvImporting(true);
    try {
      const res = await importPackagesCsv({ packages: csvData });
      setCsvResult(res.data);
      if (res.data.created_count > 0) {
        toast.success(`${res.data.created_count} ${tx('packages imported', 'paquetes importados', 'packages imported')}`);
        fetchData();
      }
      if (res.data.error_count > 0) {
        toast.error(`${res.data.error_count} ${tx('errors', 'errores', 'Erreurs')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Import failed', 'Error al importar', 'Import failed')));
    } finally {
      setCsvImporting(false);
    }
  };

  // Barcode Scanner
  const handleBarcodeDetected = async (code) => {
    try {
      const response = await scanPackage(code);
      setSelectedPackage(response.data);
      setShowDetailModal(true);
      loadDeliveryProof(response.data.id);
      setShowScanner(false);
    } catch (error) {
      toast.error(tx('Package not found', 'Paquete no encontrado', 'Package not found'));
      setShowScanner(false);
    }
  };

  const handleWeightChange = (lbs) => {
    setFormData({ ...formData, weight_lbs: lbs, weight_kg: lbs ? (parseFloat(lbs) * 0.453592).toFixed(2) : '' });
  };

  const getStatusBadgeClass = (status) => `status-${status}`;
  const nextStatus = getNextBatchStatus();

  return (
    <Layout title={t('packages')}>
      <div className="space-y-6 premium-fade-up" data-testid="packages-page">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto flex-wrap">
            <div className="relative flex-1 sm:max-w-xs">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder={t('searchPackages')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-lg"
                data-testid="package-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 rounded-lg" data-testid="status-filter">
                <SelectValue placeholder={tx('All Status', 'Todos', 'All Status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tx('All Status', 'Todos', 'All Status')}</SelectItem>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {language === 'fr' ? (opt.label_fr || opt.label_en) : language === 'es' ? opt.label_es : opt.label_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-52 rounded-lg" data-testid="package-customer-filter">
                <SelectValue placeholder={tx('All Clients', 'Todos los Clientes', 'Tous Clients')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tx('All Clients', 'Todos los Clientes', 'Tous Clients')}</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={() => setShowScanner(true)}
              variant="outline"
              className="rounded-lg"
              data-testid="scan-package-btn"
            >
              <Barcode size={18} className="mr-2" />
              {t('scanPackage')}
            </Button>
            {!isWarehouse && (
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="rounded-lg border-purple-300 text-purple-600 hover:bg-purple-50"
                data-testid="csv-import-btn"
              >
                <UploadSimple size={18} className="mr-2" />
                {tx('Import CSV', 'Importar CSV', 'Importer CSV')}
              </Button>
            )}
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            {!isWarehouse && (
              <Button 
                onClick={() => setShowBulkModal(true)}
                variant="outline"
                className="rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="bulk-add-btn"
              >
                <Plus size={18} className="mr-2" />
                {tx('Bulk Add', 'Agregar en Lote', 'Ajout en lot')}
              </Button>
            )}
            {!isWarehouse && (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="ship-gradient text-white rounded-lg"
                data-testid="create-package-btn"
              >
                <Plus size={18} className="mr-2" />
                {t('newPackage')}
              </Button>
            )}
          </div>
        </div>

        {/* Batch Action Bar */}
        <AnimatePresence>
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center gap-3 bg-[#0077FF]/10 border border-[#0077FF]/30 px-4 py-3 rounded-xl"
              data-testid="batch-action-bar"
            >
              <CheckSquare size={20} className="text-[#0077FF]" weight="fill" />
              <span className="font-medium text-[#0077FF]">
                {selectedIds.size} {tx('selected', 'seleccionados', 'sélectionné(s)')}
              </span>
              
              <div className="flex-1" />
              
              {nextStatus ? (
                <Button
                  onClick={handleBatchAdvance}
                  disabled={batchLoading}
                  className="ship-gradient text-white rounded-lg"
                  data-testid="batch-advance-btn"
                >
                  <ArrowRight size={18} className="mr-2" />
                  {batchLoading ? '...' : (
                    language === 'es' 
                      ? `Avanzar a ${STATUS_OPTIONS.find(o => o.value === nextStatus)?.label_es}`
                      : `Advance to ${STATUS_OPTIONS.find(o => o.value === nextStatus)?.label_en}`
                  )}
                </Button>
              ) : (
                <Select onValueChange={handleBatchSetStatus}>
                  <SelectTrigger className="w-52 rounded-lg" data-testid="batch-status-select">
                    <SelectValue placeholder={tx('Set status to...', 'Cambiar estado a...', 'Set status to...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {language === 'fr' ? (opt.label_fr || opt.label_en) : language === 'es' ? opt.label_es : opt.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button
                onClick={() => setSelectedIds(new Set())}
                variant="ghost"
                size="sm"
                className="text-gray-500 rounded-lg"
              >
                <X size={16} className="mr-1" />
                {tx('Clear', 'Deseleccionar', 'Clear')}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active / Completed tabs */}
        <div className="inline-flex bg-gray-100 rounded-lg p-1 text-xs font-bold mb-4" data-testid="package-tab-filter">
          {[
            { id: 'active', label: tx('Active', 'Activos', 'Actifs') },
            { id: 'completed', label: tx('Completed', 'Completados', 'Complétés') },
          ].map(o => (
            <button
              key={o.id}
              onClick={() => setTabFilter(o.id)}
              className={`px-4 py-1.5 rounded-md transition-colors ${tabFilter === o.id ? 'bg-white text-[#0077FF] shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
              data-testid={`pkg-tab-${o.id}`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Packages Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="kpi-card overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : packages.length > 0 ? (() => {
            const isCompleted = (p) => p.status === 'delivered';
            const visiblePackages = packages.filter(p => tabFilter === 'completed' ? isCompleted(p) : !isCompleted(p));
            if (visiblePackages.length === 0) {
              return (
                <div className="text-center py-16 text-gray-500">
                  <Package size={48} className="mx-auto mb-3 opacity-50" />
                  <p className="font-medium">
                    {tabFilter === 'completed'
                      ? tx('No completed packages yet', 'No hay paquetes completados', 'Aucun colis complété')
                      : tx('No active packages', 'No hay paquetes activos', 'Aucun colis actif')}
                  </p>
                </div>
              );
            }
            return (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-center py-3 px-2 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === visiblePackages.length && visiblePackages.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 accent-[#0077FF] cursor-pointer"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left py-3 px-4">{t('trackingNumber')}</th>
                    <th className="text-left py-3 px-4">{t('customer')}</th>
                    <th className="text-left py-3 px-4">{t('description')}</th>
                    <th className="text-left py-3 px-4">{t('weight')}</th>
                    <th className="text-left py-3 px-4">{t('status')}</th>
                    <th className="text-right py-3 px-4">{t('cost')}</th>
                    <th className="text-center py-3 px-4">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePackages.map((pkg) => (
                    <tr 
                      key={pkg.id} 
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedIds.has(pkg.id) ? 'bg-[#0077FF]/5' : ''}`}
                    >
                      <td className="py-3 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(pkg.id)}
                          onChange={() => toggleSelect(pkg.id)}
                          className="w-4 h-4 accent-[#0077FF] cursor-pointer"
                          data-testid={`select-pkg-${pkg.id}`}
                        />
                      </td>
                      <td className="py-3 px-4 font-mono text-sm font-medium">{pkg.tracking_number}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900" data-testid={`pkg-customer-name-${pkg.id}`}>
                          {pkg.customer_name}
                        </div>
                        {(pkg.customer_address || pkg.customer_city) && (
                          <div className="text-[11px] text-gray-500 leading-tight mt-0.5 max-w-[220px] truncate" data-testid={`pkg-customer-address-${pkg.id}`}>
                            {pkg.customer_address}
                            {pkg.customer_address && (pkg.customer_city || pkg.customer_state) ? ', ' : ''}
                            {[pkg.customer_city, pkg.customer_state].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {pkg.customer_pin_location && (
                          <a
                            href={pkg.customer_pin_location}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-1 mr-1 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-medium hover:bg-rose-100 hover:border-rose-300 transition-colors"
                            data-testid={`pkg-pin-link-${pkg.id}`}
                          >
                            <MapPin size={11} weight="fill" />
                            {tx('Open Pin in Maps', 'Abrir Pin en Maps', 'Voir le pin')}
                          </a>
                        )}
                        {pkg.customer_tracking_link_id && (
                          <a
                            href={`/portal/submit/${pkg.customer_tracking_link_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                            data-testid={`pkg-tracking-upload-${pkg.id}`}
                            title={tx('Enter tracking on this client\'s upload page', 'Ingresar tracking en la página del cliente', 'Saisir tracking pour ce client')}
                          >
                            <UploadSimple size={11} weight="bold" />
                            {tx('Enter Tracking', 'Ingresar Tracking', 'Saisir Tracking')}
                          </a>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm max-w-xs truncate">{pkg.description}</td>
                      <td className="py-3 px-4 text-sm">
                        <div>{pkg.weight_lbs} lbs</div>
                        <div className="text-xs text-gray-500">
                          {pkg.weight_kg ? `${pkg.weight_kg} kg` : `${(pkg.weight_lbs * 0.453592).toFixed(2)} kg`}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Select 
                          value={pkg.status} 
                          onValueChange={(status) => handleUpdateStatus(pkg.id, status)}
                        >
                          <SelectTrigger className={`w-44 h-8 text-xs rounded-lg ${getStatusBadgeClass(pkg.status)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {language === 'fr' ? (opt.label_fr || opt.label_en) : language === 'es' ? opt.label_es : opt.label_en}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3 px-4 text-right font-bold">
                        <div className="flex items-center justify-end gap-1">
                          <span>${pkg.shipping_cost}</span>
                          {pkg.price_override && (
                            <span
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                              title={pkg.price_override_reason || tx('Manually overridden', 'Manualmente ajustado', 'Ajusté manuellement')}
                              data-testid={`price-override-badge-${pkg.id}`}
                            >
                              ★
                            </span>
                          )}
                          {!isWarehouse && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openPriceOverride(pkg); }}
                              className="p-1 hover:bg-amber-50 rounded-md transition-colors"
                              title={tx('Edit price', 'Editar precio', 'Modifier prix')}
                              data-testid={`edit-price-${pkg.id}`}
                            >
                              <PencilSimple size={13} className="text-amber-600" weight="bold" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => { setSelectedPackage(pkg); setShowDetailModal(true); loadDeliveryProof(pkg.id); }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg"
                            data-testid={`view-package-${pkg.id}`}
                          >
                            <Eye size={18} className="text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(pkg.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg"
                            data-testid={`delete-package-${pkg.id}`}
                          >
                            <Trash size={18} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })() : (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t('noPackages')}</p>
              <p className="text-sm mt-1">{t('createFirstPackage')}</p>
            </div>
          )}
        </motion.div>

        {/* Bulk Add Modal */}
        <Dialog open={showBulkModal} onOpenChange={(v) => { if (!v) closeBulkModal(); }}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bulk-add-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Bulk Add Trackings', 'Agregar Trackings en Lote', 'Ajout en lot')}
              </DialogTitle>
              <p className="text-xs text-gray-500 mt-1">
                {tx(
                  'Pick a client, paste carrier tracking numbers (one per line), and one package will be logged per tracking with the shared weight & description.',
                  'Elige un cliente, pega números de tracking (uno por línea) y se creará un paquete por cada uno con el mismo peso y descripción.',
                  'Choisissez un client, collez les trackings et les colis seront créés en lot.'
                )}
              </p>
            </DialogHeader>
            <form onSubmit={handleBulkAdd} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">{t('customer')}</Label>
                <Select value={bulkForm.customer_id} onValueChange={(v) => setBulkForm({ ...bulkForm, customer_id: v })}>
                  <SelectTrigger className="rounded-lg" data-testid="bulk-customer-select">
                    <SelectValue placeholder={t('selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{tx('Weight per pkg (lbs)', 'Peso por paquete (lbs)', 'Poids par colis (lbs)')}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={bulkForm.weight_lbs}
                    onChange={(e) => setBulkForm({ ...bulkForm, weight_lbs: e.target.value })}
                    placeholder="0.0"
                    className="rounded-lg"
                    data-testid="bulk-weight-input"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    {tx('Optional. You can update per-package weight later.', 'Opcional. Puedes actualizar el peso después.', 'Optionnel — modifiable par colis ensuite.')}
                  </p>
                </div>
                <div>
                  <Label className="form-label">{tx('Description (shared)', 'Descripción (compartida)', 'Description (partagée)')}</Label>
                  <Input
                    value={bulkForm.description}
                    onChange={(e) => setBulkForm({ ...bulkForm, description: e.target.value })}
                    placeholder={tx('E.g. Amazon intake', 'Ej: Intake de Amazon', 'E.g. Intake Amazon')}
                    className="rounded-lg"
                    data-testid="bulk-desc-input"
                  />
                </div>
              </div>
              <div>
                <Label className="form-label">
                  {tx('Tracking numbers (one per line)', 'Números de tracking (uno por línea)', 'Trackings (un par ligne)')}
                </Label>
                <textarea
                  value={bulkForm.tracking_numbers_raw}
                  onChange={(e) => setBulkForm({ ...bulkForm, tracking_numbers_raw: e.target.value })}
                  placeholder={"1Z999AA10123456784\nTBA282849912345\n9400111899223197428437"}
                  rows={10}
                  className="w-full rounded-lg p-3 text-sm font-mono border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0077FF]/40"
                  data-testid="bulk-trackings-input"
                  required
                />
                {(() => {
                  const c = (bulkForm.tracking_numbers_raw || '').split(/[\n,;\t]/).map(s => s.trim()).filter(Boolean).length;
                  return (
                    <p className="text-[11px] text-gray-500 mt-1">
                      {c} {tx('trackings ready', 'trackings listos', 'prêts')}
                    </p>
                  );
                })()}
              </div>

              {bulkResult && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs space-y-1">
                  <p><strong>{bulkResult.created_count}</strong> {tx('created', 'creados', 'créés')} · <strong>${bulkResult.total_charge_usd.toFixed(2)}</strong> {tx('charged', 'cobrado', 'facturé')}</p>
                  {bulkResult.skipped_count > 0 && (
                    <details>
                      <summary className="cursor-pointer text-amber-700 font-semibold">{bulkResult.skipped_count} {tx('skipped', 'omitidos', 'ignorés')}</summary>
                      <ul className="mt-1 list-disc list-inside text-[11px]">
                        {(bulkResult.skipped || []).slice(0, 10).map((s, i) => (
                          <li key={i}><span className="font-mono">{s.tracking_number}</span> — {s.reason}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeBulkModal}
                  className="rounded-lg"
                  data-testid="bulk-cancel-btn"
                >
                  {tx('Close', 'Cerrar', 'Fermer')}
                </Button>
                <Button
                  type="submit"
                  disabled={bulkSubmitting}
                  className="ship-gradient text-white rounded-lg"
                  data-testid="bulk-submit-btn"
                >
                  {bulkSubmitting
                    ? tx('Adding…', 'Agregando…', 'Ajout…')
                    : tx('Add All', 'Agregar Todos', 'Tout ajouter')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {t('newPackage')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">{t('customer')}</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({...formData, customer_id: v})}>
                  <SelectTrigger className="rounded-lg" data-testid="customer-select">
                    <SelectValue placeholder={t('selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">{t('description')}</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder={tx('E.g. Clothes, electronics...', 'Ej: Ropa, electronicos...', 'E.g. Clothes, electronics...')} className="rounded-lg" required data-testid="package-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('weightLbs')}</Label>
                  <Input type="number" step="0.1" value={formData.weight_lbs} onChange={(e) => handleWeightChange(e.target.value)} placeholder="0.0" className="rounded-lg" required data-testid="package-weight-lbs" />
                </div>
                <div>
                  <Label className="form-label">{t('weightKg')}</Label>
                  <Input type="number" step="0.01" value={formData.weight_kg} onChange={(e) => setFormData({...formData, weight_kg: e.target.value})} placeholder="0.00" className="rounded-lg bg-gray-50" readOnly />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('dimensions')}</Label>
                  <Input value={formData.dimensions} onChange={(e) => setFormData({...formData, dimensions: e.target.value})} placeholder="L x W x H" className="rounded-lg" />
                </div>
                <div>
                  <Label className="form-label">{t('declaredValue')}</Label>
                  <Input type="number" step="0.01" value={formData.declared_value} onChange={(e) => setFormData({...formData, declared_value: e.target.value})} placeholder="$0.00" className="rounded-lg" />
                </div>
              </div>
              <div>
                <Label className="form-label">{t('notes')}</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder={tx('Special instructions...', 'Instrucciones especiales...', 'Special instructions...')} className="rounded-lg" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-lg">{t('cancel')}</Button>
                <Button type="submit" className="ship-gradient text-white rounded-lg" data-testid="submit-package-btn">{t('create')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Price Override Modal */}
        <Dialog open={!!priceOverridePkg} onOpenChange={(o) => { if (!o) closePriceOverride(); }}>
          <DialogContent className="max-w-md" data-testid="price-override-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
                <CurrencyDollar size={22} className="text-amber-600" weight="fill" />
                {tx('Adjust Shipping Price', 'Ajustar Precio de Envío', 'Ajuster le Prix')}
              </DialogTitle>
              {priceOverridePkg && (
                <p className="text-xs text-gray-500 mt-1 font-mono">
                  {priceOverridePkg.tracking_number} · {priceOverridePkg.weight_kg || '?'} kg
                </p>
              )}
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {priceOverridePkg && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{tx('Current price', 'Precio actual', 'Prix actuel')}</span>
                    <span className="font-bold">${Number(priceOverridePkg.shipping_cost || 0).toFixed(2)}</span>
                  </div>
                  {priceOverridePkg.price_override && (
                    <p className="text-[10px] text-amber-700 mt-1">
                      ★ {tx('Already manually overridden', 'Ya ajustado manualmente', 'Déjà ajusté')}
                      {priceOverridePkg.price_override_reason ? `: ${priceOverridePkg.price_override_reason}` : ''}
                    </p>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-gray-700">
                  {tx('New Shipping Price (USD)', 'Nuevo Precio (USD)', 'Nouveau prix (USD)')}
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={priceOverrideDraft.price}
                    onChange={(e) => setPriceOverrideDraft(d => ({ ...d, price: e.target.value }))}
                    placeholder="0.00"
                    className="pl-7 rounded-lg"
                    autoFocus
                    data-testid="price-override-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">
                  {tx('Reason (optional)', 'Motivo (opcional)', 'Motif (optionnel)')}
                </Label>
                <Input
                  type="text"
                  value={priceOverrideDraft.reason}
                  onChange={(e) => setPriceOverrideDraft(d => ({ ...d, reason: e.target.value }))}
                  placeholder={tx('e.g. Damaged box, regular customer discount', 'Ej: Caja dañada, descuento cliente regular', "Ex : carton abîmé, remise client")}
                  maxLength={300}
                  className="rounded-lg mt-1"
                  data-testid="price-override-reason-input"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {tx(
                    'Saved on the package as an audit note. The customer balance auto-adjusts.',
                    'Se guarda como nota en el paquete. El saldo del cliente se ajusta automáticamente.',
                    'Enregistré comme note dʼaudit. Le solde client est ajusté.'
                  )}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={closePriceOverride} className="rounded-lg" disabled={savingPriceOverride}>
                  {t('cancel')}
                </Button>
                <Button
                  type="button"
                  disabled={savingPriceOverride}
                  onClick={savePriceOverride}
                  className="rounded-lg bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="save-price-override-btn"
                >
                  {savingPriceOverride ? '...' : tx('Save Price', 'Guardar', 'Enregistrer')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Package Details', 'Detalles del Paquete', 'Détails du Colis')}
              </DialogTitle>
            </DialogHeader>
            {selectedPackage && (
              <div className="space-y-4 mt-4">
                <div className="ship-gradient text-white p-4 rounded-xl">
                  <p className="text-xs uppercase tracking-wide text-white/70 mb-1">{t('trackingNumber')}</p>
                  <p className="font-mono text-xl font-bold">{selectedPackage.tracking_number}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('customer')}</p>
                    <p className="font-medium">{selectedPackage.customer_name}</p>
                    <p className="text-sm text-gray-500">{selectedPackage.customer_email}</p>
                    {selectedPackage.customer_phone && (
                      <p className="text-sm text-gray-500">{selectedPackage.customer_phone}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('status')}</p>
                    <span className={`status-badge ${getStatusBadgeClass(selectedPackage.status)}`}>
                      {getStatusLabel(selectedPackage.status, language)}
                    </span>
                  </div>
                  {(selectedPackage.customer_address || selectedPackage.customer_city) && (
                    <div className="col-span-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{tx('Delivery Address', 'Dirección de Entrega', 'Adresse de livraison')}</p>
                      <p className="text-sm">
                        {selectedPackage.customer_address}
                        {selectedPackage.customer_address && (selectedPackage.customer_city || selectedPackage.customer_state) ? ', ' : ''}
                        {[selectedPackage.customer_city, selectedPackage.customer_state].filter(Boolean).join(', ')}
                      </p>
                      {selectedPackage.customer_pin_location && (
                        <a
                          href={selectedPackage.customer_pin_location}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-1 mr-2 text-xs text-rose-600 hover:text-rose-700 hover:underline"
                          data-testid="package-pin-link"
                        >
                          <MapPin size={12} weight="fill" />
                          {tx('Open pin location in Maps', 'Abrir ubicación en Maps', "Voir le pin sur Maps")}
                        </a>
                      )}
                      {selectedPackage.customer_tracking_link_id && (
                        <a
                          href={`/portal/submit/${selectedPackage.customer_tracking_link_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
                          data-testid="package-tracking-upload-link"
                        >
                          <UploadSimple size={12} weight="bold" />
                          {tx('Enter Tracking for this client', 'Ingresar Tracking del cliente', 'Saisir Tracking client')}
                        </a>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('weight')}</p>
                    <p className="font-medium">
                      {selectedPackage.weight_lbs} lbs
                      <span className="text-sm text-gray-500 ml-2">
                        ({selectedPackage.weight_kg || (selectedPackage.weight_lbs * 0.453592).toFixed(2)} kg)
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('cost')}</p>
                    <p className="text-xl font-bold text-[#0077FF]">${(selectedPackage.shipping_cost + (selectedPackage.transport_fee || 0)).toFixed(2)}</p>
                    {!!selectedPackage.transport_fee && (
                      <p className="text-[10px] text-gray-500">${selectedPackage.shipping_cost} + ${selectedPackage.transport_fee} transport</p>
                    )}
                    {selectedPackage.price_override && (
                      <p className="text-[10px] text-amber-700 mt-0.5 italic">
                        ★ {tx('Manual price override', 'Precio ajustado manualmente', 'Prix ajusté')}{selectedPackage.price_override_reason ? `: ${selectedPackage.price_override_reason}` : ''}
                      </p>
                    )}
                    {!isWarehouse && (
                      <button
                        onClick={() => openPriceOverride(selectedPackage)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 hover:text-amber-900 hover:underline"
                        data-testid="modal-edit-price-btn"
                      >
                        <PencilSimple size={11} weight="bold" />
                        {tx('Adjust shipping price', 'Ajustar precio de envío', 'Ajuster prix')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Per-package transport fee — admins only */}
                {!isWarehouse && (
                  <div className="border-t pt-3">
                    <Label className="text-xs uppercase tracking-wide text-gray-500 mb-1 block">
                      {tx('Transport Fee (per package)', 'Tarifa de Transporte (por paquete)', 'Frais de transport')}
                    </Label>
                    <div className="flex gap-2 items-center">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          defaultValue={selectedPackage.transport_fee || ''}
                          placeholder="0.00"
                          className="pl-7"
                          onChange={(e) => setSelectedPackage({ ...selectedPackage, transport_fee_draft: e.target.value })}
                          data-testid="transport-fee-input"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          const fee = parseFloat(selectedPackage.transport_fee_draft);
                          if (isNaN(fee) || fee < 0) {
                            toast.error(tx('Enter a valid fee', 'Ingresa una tarifa válida', 'Saisissez un montant valide'));
                            return;
                          }
                          try {
                            await updatePackage(selectedPackage.id, { transport_fee: fee });
                            toast.success(tx('Transport fee saved', 'Tarifa guardada', 'Frais enregistrés'));
                            setSelectedPackage({ ...selectedPackage, transport_fee: fee, transport_fee_draft: undefined });
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed');
                          }
                        }}
                        data-testid="save-transport-fee-btn"
                      >
                        {tx('Save Fee', 'Guardar', 'Enregistrer')}
                      </Button>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('description')}</p>
                  <p className="text-sm">{selectedPackage.description}</p>
                </div>
                {selectedPackage.notes && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('notes')}</p>
                    <p className="text-sm text-gray-600">{selectedPackage.notes}</p>
                  </div>
                )}
                <div className="border-t pt-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">{tx('Timeline', 'Historial', 'Timeline')}</p>
                  <div className="space-y-2 text-sm">
                    {selectedPackage.intake_date && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span>{tx('Intake Miami', 'Recibido Miami', 'Intake Miami')}: {new Date(selectedPackage.intake_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedPackage.in_transit_date && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-500 rounded-full" />
                        <span>{tx('In Transit', 'En Transito', 'In Transit')}: {new Date(selectedPackage.in_transit_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedPackage.arrived_date && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-teal-500 rounded-full" />
                        <span>{tx('Arrived San Jose', 'Llego San Jose', 'Arrived San Jose')}: {new Date(selectedPackage.arrived_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedPackage.ready_date && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span>{tx('Ready for Pickup', 'Listo para Recoger', 'Ready for Pickup')}: {new Date(selectedPackage.ready_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedPackage.delivered_date && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                        <span>{tx('Delivered', 'Entregado', 'Livré')}: {new Date(selectedPackage.delivered_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Proof */}
                <div className="mt-4 p-4 bg-gray-50 rounded-xl" data-testid="delivery-proof-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Camera size={18} className="text-gray-600" />
                      <span className="font-semibold text-sm">{tx('Delivery Proof', 'Prueba de Entrega', 'Preuve de Livraison')}</span>
                    </div>
                    <input
                      type="file"
                      ref={proofInputRef}
                      onChange={handleProofUpload}
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      data-testid="proof-upload-input"
                    />
                    <Button
                      onClick={() => proofInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                      disabled={proofLoading}
                      className="rounded-lg text-xs"
                      data-testid="upload-proof-btn"
                    >
                      <Camera size={14} className="mr-1" />
                      {proofLoading ? '...' : (tx('Upload Photo', 'Subir Foto', 'Télécharger une Photo'))}
                    </Button>
                  </div>
                  {deliveryProof?.photo_data ? (
                    <div>
                      <img
                        src={deliveryProof.photo_data}
                        alt="Delivery proof"
                        className="w-full max-h-64 object-contain rounded-lg border border-gray-200"
                        data-testid="delivery-proof-image"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        {tx('Uploaded', 'Subido', 'Téléchargé')}: {deliveryProof.uploaded_at ? new Date(deliveryProof.uploaded_at).toLocaleString() : ''}
                        {deliveryProof.uploaded_by ? ` by ${deliveryProof.uploaded_by}` : ''}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                      <Image size={32} className="mb-2" />
                      <p className="text-xs">{tx('No delivery proof yet', 'Sin prueba de entrega', 'Pas encore de preuve')}</p>
                      <p className="text-[10px] text-gray-300 mt-1">{tx('Upload a photo as evidence', 'Sube una foto como evidencia', 'Téléchargez une photo comme preuve')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {selectedPackage && !isWarehouse && (
              <div className="border-t pt-4 mt-4 flex flex-col sm:flex-row gap-2">
                <Button
                  className="flex-1 ship-gradient text-white"
                  disabled={sendingInvoice || !selectedPackage.customer_email || (selectedPackage.shipping_cost || 0) <= 0}
                  onClick={() => handleSendPackageInvoice(selectedPackage)}
                  data-testid="send-invoice-btn"
                >
                  {sendingInvoice
                    ? tx('Sending...', 'Enviando...', 'Envoi...')
                    : tx('Send Invoice to Customer', 'Enviar Factura al Cliente', 'Envoyer la facture')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* CSV Import Modal */}
        <Dialog open={showCsvModal} onOpenChange={setShowCsvModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('IMPORT PACKAGES (CSV)', 'IMPORTAR PAQUETES (CSV)', 'IMPORT PACKAGES (CSV)')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* CSV Template info */}
              <div className="bg-gray-50 p-4 rounded-xl text-sm">
                <p className="font-medium mb-2">{tx('Required CSV format:', 'Formato CSV requerido:', 'Required CSV format:')}</p>
                <code className="block bg-gray-100 p-2 rounded text-xs font-mono">
                  customer_email,description,weight_lbs,dimensions,declared_value,notes
                </code>
              </div>

              {csvErrors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl">
                  <div className="flex items-center gap-2 text-amber-700 mb-1">
                    <Warning size={18} />
                    <span className="font-medium text-sm">{csvErrors.length} {tx('rows with errors', 'filas con errores', 'rows with errors')}</span>
                  </div>
                  {csvErrors.slice(0, 3).map((err, i) => (
                    <p key={i} className="text-xs text-amber-600">Row {err.row}: {err.msg}</p>
                  ))}
                </div>
              )}

              {/* Preview table */}
              {csvData.length > 0 && !csvResult && (
                <>
                  <p className="text-sm font-medium text-gray-700">
                    {csvData.length} {tx('packages ready to import', 'paquetes listos para importar', 'packages ready to import')}
                  </p>
                  <div className="overflow-x-auto max-h-64 border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">{tx('Description', 'Descripcion', 'Description')}</th>
                          <th className="px-3 py-2 text-right">{tx('Weight (lbs)', 'Peso (lbs)', 'Weight (lbs)')}</th>
                          <th className="px-3 py-2 text-right">{tx('Est. Cost', 'Costo Est.', 'Est. Cost')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0, 50).map((row, i) => (
                          <tr key={i} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                            <td className="px-3 py-1.5">{row.customer_email}</td>
                            <td className="px-3 py-1.5">{row.description}</td>
                            <td className="px-3 py-1.5 text-right">{row.weight_lbs}</td>
                            <td className="px-3 py-1.5 text-right font-medium text-[#0077FF]">${(row.weight_lbs * 4.5).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowCsvModal(false)} className="rounded-lg">{t('cancel')}</Button>
                    <Button 
                      onClick={handleCsvImport} 
                      disabled={csvImporting}
                      className="ship-gradient text-white rounded-lg"
                      data-testid="confirm-csv-import-btn"
                    >
                      <UploadSimple size={18} className="mr-2" />
                      {csvImporting ? '...' : (tx(`Import ${csvData.length} Packages`, `Importar ${csvData.length} Paquetes`, `Import ${csvData.length} Packages`))}
                    </Button>
                  </div>
                </>
              )}

              {/* Import Result */}
              {csvResult && (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 p-4 rounded-xl" data-testid="csv-import-result">
                    <p className="font-bold text-green-700 text-lg">{csvResult.created_count} {tx('packages created', 'paquetes creados', 'packages created')}</p>
                    {csvResult.error_count > 0 && (
                      <p className="text-red-600 text-sm mt-1">{csvResult.error_count} {tx('errors', 'errores', 'Erreurs')}</p>
                    )}
                  </div>
                  
                  {csvResult.created?.length > 0 && (
                    <div className="overflow-x-auto max-h-48 border rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Tracking</th>
                            <th className="px-3 py-2 text-left">{t('customer')}</th>
                            <th className="px-3 py-2 text-right">{t('weight')}</th>
                            <th className="px-3 py-2 text-right">{t('cost')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvResult.created.map((row, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-3 py-1.5 font-mono">{row.tracking_number}</td>
                              <td className="px-3 py-1.5">{row.customer}</td>
                              <td className="px-3 py-1.5 text-right">{row.weight_lbs} lbs</td>
                              <td className="px-3 py-1.5 text-right font-bold text-[#0077FF]">${row.shipping_cost.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {csvResult.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 p-3 rounded-xl">
                      <p className="font-medium text-red-700 text-sm mb-1">{tx('Errors:', 'Errores:', 'Errors:')}</p>
                      {csvResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-600">Row {err.row} ({err.email}): {err.error}</p>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <Button onClick={() => { setShowCsvModal(false); setCsvResult(null); setCsvData([]); }} className="ship-gradient text-white rounded-lg">
                      {tx('Done', 'Cerrar', 'Fermer')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Barcode Scanner */}
        <AnimatePresence>
          {showScanner && (
            <BarcodeScanner
              onDetected={handleBarcodeDetected}
              onClose={() => setShowScanner(false)}
              language={language}
            />
          )}
        </AnimatePresence>

        {/* Weight Prompt Modal — shown when marking a package as Received at Warehouse */}
        <Dialog open={!!weightPromptPkg} onOpenChange={(o) => !o && setWeightPromptPkg(null)}>
          <DialogContent className="max-w-md" data-testid="weight-prompt-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Confirm Final Weight', 'Confirmar Peso Final', 'Confirmer le poids final')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {tx(
                  'Reweigh the package at the San Jose warehouse and enter the final weight. This sets the shipping cost on the customer invoice.',
                  'Pesa el paquete en el almacén de San José e ingresa el peso final. Esto define el costo de envío en la factura del cliente.',
                  "Pesez le colis à l'entrepôt de San José. Cela définit le coût sur la facture du client."
                )}
              </p>
              {weightPromptPkg && (
                <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 space-y-0.5">
                  <div><span className="text-gray-400">Tracking:</span> <span className="font-mono font-semibold text-gray-900">{weightPromptPkg.tracking_number}</span></div>
                  <div><span className="text-gray-400">Customer:</span> <span className="font-semibold text-gray-900">{weightPromptPkg.customer_name}</span></div>
                  {weightPromptPkg.weight_lbs ? (
                    <div className="text-amber-600"><span className="text-gray-400">Originally declared:</span> {weightPromptPkg.weight_lbs} lbs</div>
                  ) : null}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="form-label">{t('weightLbs') || 'Weight (lbs)'}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={weightPromptValue.lbs}
                    onChange={(e) => {
                      const lbs = e.target.value;
                      setWeightPromptValue({
                        lbs,
                        kg: lbs ? (parseFloat(lbs) * 0.453592).toFixed(2) : '',
                      });
                    }}
                    placeholder="0.00"
                    autoFocus
                    data-testid="weight-prompt-lbs"
                  />
                </div>
                <div>
                  <Label className="form-label">{tx('Weight (kg)', 'Peso (kg)', 'Poids (kg)')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={weightPromptValue.kg}
                    onChange={(e) => {
                      const kg = e.target.value;
                      setWeightPromptValue({
                        kg,
                        lbs: kg ? (parseFloat(kg) / 0.453592).toFixed(2) : '',
                      });
                    }}
                    placeholder="0.00"
                    data-testid="weight-prompt-kg"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setWeightPromptPkg(null)}
                  disabled={savingWeight}
                  data-testid="weight-prompt-cancel"
                >
                  {tx('Cancel', 'Cancelar', 'Annuler')}
                </Button>
                <Button
                  className="flex-1 ship-gradient text-white"
                  onClick={handleConfirmWeightAndIntake}
                  disabled={savingWeight}
                  data-testid="weight-prompt-confirm"
                >
                  {savingWeight ? '...' : tx('Save & Mark Arrived', 'Guardar y Marcar Llegado', 'Enregistrer & Marquer arrivé')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
